/**
 * SystemFieldsSection — read-only preview of the fixed core contract fields.
 *
 * Rendered at the top of the canvas so the builder shows the *whole* form the
 * end user will fill in. Mirrors ContractForm's four core sections (General /
 * Counterparty / Dates / Financial) with the same titles, colors and fields.
 * Everything here is locked: no drag handle, no delete, no type/width/required
 * controls, and it is not a drop target. Each group can only be collapsed to
 * get it out of the way.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RiLock2Line, RiArrowDownSLine, RiArrowUpSLine, RiLayoutRowLine,
} from 'react-icons/ri';

import { getFieldTypeInfo, fieldTypeLabel } from './utils';
import { SYSTEM_SECTIONS } from './systemFields';

const SPAN_BY_WIDTH = { full: 12, half: 6, third: 4, quarter: 3 };

function LockedInput({ type, t }) {
  switch (type) {
    case 'date':
      return <input type="date" className="fm-wys-input" disabled />;
    case 'number':
      return <input type="number" className="fm-wys-input" placeholder="0" disabled />;
    case 'email':
      return <input type="email" className="fm-wys-input" placeholder="name@example.com" disabled />;
    case 'phone':
      return <input type="tel" className="fm-wys-input" placeholder="+1 (555) 000-0000" disabled />;
    case 'currency':
      return (
        <div className="fm-wys-currency">
          <span>$</span>
          <input type="number" className="fm-wys-input" placeholder="0.00" disabled />
        </div>
      );
    case 'checkbox':
      return (
        <label className="fm-wys-checkbox">
          <input type="checkbox" disabled />
          <span>{t('fieldsManager.fieldRow.checkboxOption')}</span>
        </label>
      );
    case 'textarea':
      return <textarea className="fm-wys-input fm-wys-input--textarea" rows={2} disabled />;
    case 'select':
      return (
        <select className="fm-wys-input fm-wys-select" disabled defaultValue="">
          <option value="">{t('fieldsManager.fieldRow.selectAnOption')}</option>
        </select>
      );
    default:
      return <input type="text" className="fm-wys-input" disabled />;
  }
}

function SystemGroup({ section, note }) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div
      className="fm-section fm-section--system"
      style={{ '--section-color': section.color }}
    >
      <div className="fm-section__header">
        <span className="fm-section__lock" title={t('fieldsManager.systemSection.lockedHint')}>
          <RiLock2Line />
        </span>

        <div className="fm-section__title-wrap">
          <span className="fm-section__title-static">
            {t(section.titleKey)}
          </span>
        </div>

        <span className="fm-section-type-badge fm-section-type-badge--fields">
          <RiLayoutRowLine /> {t('fieldsManager.sectionCard.fields')}
        </span>

        <span className="fm-section-locked-badge">
          <RiLock2Line /> {t('fieldsManager.systemSection.badge')}
        </span>

        <div className="fm-section__actions">
          <button
            type="button"
            className="fm-icon-btn fm-icon-btn--ghost"
            onClick={() => setCollapsed((v) => !v)}
            title={collapsed
              ? t('fieldsManager.systemSection.expand')
              : t('fieldsManager.systemSection.collapse')}
          >
            {collapsed ? <RiArrowDownSLine /> : <RiArrowUpSLine />}
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="fm-section__body">
          {note && (
            <p className="fm-section__system-note">
              {t('fieldsManager.systemSection.note')}
            </p>
          )}
          <div className="fm-field-grid">
            {section.fields.map((field) => {
              const typeInfo = getFieldTypeInfo(field.type);
              return (
                <div
                  key={field.id}
                  className="fm-field fm-field--locked"
                  style={{
                    '--field-color': typeInfo.color,
                    '--field-bg': typeInfo.bg,
                    gridColumn: `span ${SPAN_BY_WIDTH[field.width] || 12}`,
                  }}
                >
                  <div className="fm-field__head">
                    <span className="fm-field__lock" title={t('fieldsManager.systemSection.fieldLockedHint')}>
                      <RiLock2Line />
                    </span>
                    <div className="fm-field__label-wrap">
                      <span className="fm-field__label-static">{t(field.labelKey)}</span>
                      {field.required && <span className="fm-req-star" title={t('fieldsManager.fieldRow.required')}>*</span>}
                    </div>
                    <span className="fm-field__type-static">{fieldTypeLabel(t, typeInfo)}</span>
                  </div>
                  <div className="fm-field__body">
                    <LockedInput type={field.type} t={t} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SystemFieldsSection() {
  return (
    <>
      {SYSTEM_SECTIONS.map((section, i) => (
        // The explanatory note only rides on the first group; the rest stay lean.
        <SystemGroup key={section.id} section={section} note={i === 0} />
      ))}
    </>
  );
}
